/**
 * P0 QC Workflow - QC Escalate Modal
 * 
 * Modal for escalating QC review submissions to Team Leaders.
 * Requires mandatory escalation reason field (min 10 characters).
 */

import React, { useState } from 'react';
import { ArrowUpRight, X, FileText, User, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const QCEscalateModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !order) return null;

  const MIN_REASON_LENGTH = 10;
  const isReasonValid = reason.trim().length >= MIN_REASON_LENGTH;

  const handleConfirm = async () => {
    if (!isReasonValid) {
      setError(`Escalation reason must be at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await onConfirm(reason.trim());
      setReason('');
    } catch (err) {
      setError(err.message || 'Failed to escalate order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (error && e.target.value.trim().length >= MIN_REASON_LENGTH) {
      setError('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-full">
              <ArrowUpRight className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Escalate to Team Leader</h2>
              <p className="text-sm text-gray-500">Order {order.order_number}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-5">
          {/* Order info summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Product:</span>
                <span className="font-medium text-gray-900">{order.client_product || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Submitted by:</span>
                <span className="font-medium text-gray-900">{order.qc_submitted_by_name || 'Unknown'}</span>
              </div>
            </div>
            {order.property_display && (
              <div className="mt-2 text-sm">
                <span className="text-gray-500">Property:</span>
                <span className="ml-2 font-medium text-gray-900">{order.property_display}</span>
              </div>
            )}
          </div>
          
          {/* Info message */}
          <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg mb-5">
            <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-800 font-medium">
                Escalating this order will:
              </p>
              <ul className="text-sm text-purple-700 mt-1 list-disc list-inside">
                <li>Set QC status to "Escalated"</li>
                <li>Keep the order locked</li>
                <li>Notify the appropriate Team Leader</li>
                <li>Notify the Admin team</li>
                <li>Move the order to the Escalated tab</li>
              </ul>
            </div>
          </div>
          
          {/* Mandatory escalation reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Escalation Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={handleReasonChange}
              placeholder="Please explain why this order needs Team Leader review (e.g., complex scenario, policy clarification needed, etc.)..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none ${
                error ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              rows={4}
              data-testid="qc-escalate-reason"
            />
            <div className="flex items-center justify-between mt-1">
              <p className={`text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
                {error || `Minimum ${MIN_REASON_LENGTH} characters required. Team Leaders will see this reason.`}
              </p>
              <span className={`text-xs ${reason.trim().length >= MIN_REASON_LENGTH ? 'text-green-600' : 'text-gray-400'}`}>
                {reason.trim().length}/{MIN_REASON_LENGTH}
              </span>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || !isReasonValid}
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            data-testid="qc-escalate-confirm"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Escalating...
              </>
            ) : (
              <>
                <ArrowUpRight className="w-4 h-4" />
                Escalate to Team Leader
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QCEscalateModal;
